import type { LegacyRef } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-badge';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import CardWrapper from '../CardWrapper';
import PoiDistance from '../PoiDistance';

import AirportIcon from './img/AirportIcon.svg';
import BusIcon from './img/BusIcon.svg';
import MetroIcon from './img/MetroIcon.svg';
import TrainIcon from './img/TrainIcon.svg';

import type { PoiData } from '@shared/types/frontend/nearby-transport-response';
import type { I18nService } from '@shared/types/i18n';
import type { ObjectMapping } from '@shared/types/utils';

import STYLES from './PoiCard.module.scss';

import {
  AIRPORT,
  BUS_STATION,
  TRAIN_STATION,
  METRO_STATION,
  poiSaasMap,
} from '@shared/constants/poi-types';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

const iconMap: ObjectMapping = {
  [METRO_STATION]: MetroIcon,
  [BUS_STATION]: BusIcon,
  [TRAIN_STATION]: TrainIcon,
  [AIRPORT]: AirportIcon,
};

type Props = {
  i18n: I18nService;
  poiData: PoiData;
  isSelected: boolean;
  refCallback?: LegacyRef<HTMLDivElement>;
  onClick?: () => void;
  isLast?: boolean;
};

const PoiCard = ({
  i18n,
  isLast = false,
  isSelected,
  onClick = () => {},
  poiData,
  refCallback = () => {},
}: Props) => {
  const {
    address,
    imageUrls,
    linearDistance,
    poiName,
    poiType,
    reviewCount,
    reviewScore,
  } = poiData;

  let image;
  if (imageUrls) {
    [image] = imageUrls;
  } else if (iconMap[poiType]) {
    image = iconMap[poiType];
  } else {
    image = '';
  }

  return (
    <CardWrapper
      onClick={onClick}
      isSelected={isSelected}
      refCallback={refCallback}
      image={image}
      isLast={isLast}
      className={STYLES.PoiCard}
    >
      <div>
        <BpkText
          textStyle={TEXT_STYLES.footnote}
          tagName="p"
          className={STYLES.PoiCard__contentTitle}
        >
          {poiName}
        </BpkText>
        {address && (
          <BpkText
            textStyle={TEXT_STYLES.caption}
            tagName="p"
            color={TEXT_COLORS.textSecondary}
          >
            {address}
          </BpkText>
        )}
        <div className={STYLES.PoiCard__badgeContainer}>
          {reviewScore ? (
            <BpkText
              textStyle={TEXT_STYLES.caption}
              className={STYLES.PoiCard__score}
              color={TEXT_COLORS.textPrimaryInverse}
            >
              {i18n.formatNumber(reviewScore, 1)}
              <span className={STYLES.PoiCard__scorePoints}>/5</span>
            </BpkText>
          ) : null}
          {reviewCount ? (
            <BpkText
              textStyle={TEXT_STYLES.caption}
              className={STYLES.PoiCard__count}
              color={TEXT_COLORS.textSecondary}
            >
              {i18n.translate('Reviews_label_count', {
                0: i18n.formatNumber(reviewCount, 0),
              })}
            </BpkText>
          ) : null}
          <BpkBadge
            type={BADGE_TYPES.normal}
            className={cls(
              'PoiCard__badge',
              isSelected && 'PoiCard__badgeSelected',
            )}
          >
            <BpkText
              textStyle={TEXT_STYLES.caption}
              className={cls('PoiCard__badgeText')}
              color={TEXT_COLORS.textSecondary}
            >
              {i18n.translate(poiSaasMap[poiType])}
            </BpkText>
          </BpkBadge>
        </div>
        <PoiDistance linearDistance={linearDistance} />
      </div>
    </CardWrapper>
  );
};

export default withI18n(PoiCard);
